import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  YouTube as YouTubeIcon,
  Label as LabelIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { extractAllTagsFromVideo } from '../utils/hashtagExtractor';

interface VideoTagsManagerProps {
  video: {
    id: string;
    title?: string;
    tags?: string[];
    youtubeHashtags?: string[];
    youtubeMetadata?: {
      hashtags: string[];
      title: string;
      description: string;
      channelTitle: string;
      publishedAt: string;
      viewCount: string;
      thumbnail: string;
      duration: string;
    };
  };
  onUpdateTags: (tags: string[]) => void;
  onUpdateYoutubeHashtags: (hashtags: string[]) => void;
  readOnly?: boolean;
}

const VideoTagsManager: React.FC<VideoTagsManagerProps> = ({
  video,
  onUpdateTags,
  onUpdateYoutubeHashtags,
  readOnly = false
}) => {
  const [userTags, setUserTags] = useState<string[]>(Array.isArray(video.tags) ? video.tags : []);
  const [youtubeHashtags, setYoutubeHashtags] = useState<string[]>(video.youtubeHashtags || []);
  const [newTag, setNewTag] = useState('');
  const [showYoutubeTags, setShowYoutubeTags] = useState(true);
  const [tagFilter, setTagFilter] = useState<'all' | 'user' | 'youtube'>('all');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [tagToConvert, setTagToConvert] = useState('');

  // 同步外部狀態
  useEffect(() => {
    // 確保 userTags 正確初始化，即使 video.tags 為 undefined
    const currentTags = Array.isArray(video.tags) ? video.tags : [];
    setUserTags(currentTags);
    
    // 合併所有可能的 YouTube 標籤來源
    const allYoutubeHashtags = [
      ...(video.youtubeHashtags || []),
      ...(video.youtubeMetadata?.hashtags || [])
    ];
    // 去重並保持順序
    const uniqueYoutubeHashtags = [...new Set(allYoutubeHashtags)];
    setYoutubeHashtags(uniqueYoutubeHashtags);
    
    // 使用 extractAllTagsFromVideo 獲取所有標籤
    const allTags = extractAllTagsFromVideo(video);
    
    console.log('🔍 VideoTagsManager 標籤載入:', {
      videoId: video.id,
      userTags: currentTags,
      youtubeHashtags: video.youtubeHashtags,
      youtubeMetadataHashtags: video.youtubeMetadata?.hashtags,
      mergedHashtags: uniqueYoutubeHashtags,
      videoTitle: video.title,
      youtubeMetadataTitle: video.youtubeMetadata?.title,
      allTags: allTags
    });
  }, [video.tags, video.youtubeHashtags, video.youtubeMetadata?.hashtags, video.title, video.youtubeMetadata?.title]);

  // 添加用戶標籤
  const handleAddUserTag = () => {
    if (newTag.trim() && !userTags.includes(newTag.trim())) {
      const updatedTags = [...userTags, newTag.trim()];
      setUserTags(updatedTags);
      onUpdateTags(updatedTags);
      setNewTag('');
    }
  };

  // 刪除用戶標籤
  const handleDeleteUserTag = (tagToDelete: string) => {
    const updatedTags = userTags.filter(tag => tag !== tagToDelete);
    setUserTags(updatedTags);
    onUpdateTags(updatedTags);
  };

  // 將 YouTube hashtag 轉換為用戶標籤
  const handleConvertToUserTag = (hashtag: string) => {
    if (!userTags.includes(hashtag)) {
      const updatedTags = [...userTags, hashtag];
      setUserTags(updatedTags);
      onUpdateTags(updatedTags);
    }
  };

  // 批量轉換對話框
  const handleOpenConvertDialog = (hashtag: string) => {
    setTagToConvert(hashtag);
    setConvertDialogOpen(true);
  };

  const handleConfirmConvert = () => {
    handleConvertToUserTag(tagToConvert);
    setConvertDialogOpen(false);
    setTagToConvert('');
  };

  // 過濾標籤顯示
  const getFilteredTags = () => {
    switch (tagFilter) {
      case 'user':
        return userTags;
      case 'youtube':
        return youtubeHashtags;
      default:
        return [...userTags, ...youtubeHashtags];
    }
  };

  // 檢查標籤是否為 YouTube hashtag
  const isYoutubeHashtag = (tag: string) => {
    return youtubeHashtags.includes(tag);
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* 標題和控制項 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LabelIcon />
          影片標籤管理
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* 過濾器 */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>標籤來源</InputLabel>
            <Select
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value as any)}
              label="標籤來源"
            >
              <MenuItem value="all">所有標籤</MenuItem>
              <MenuItem value="user">我的標籤</MenuItem>
              <MenuItem value="youtube">YouTube 標籤</MenuItem>
            </Select>
          </FormControl>

          {/* 顯示/隱藏 YouTube 標籤 */}
          <FormControlLabel
            control={
              <Switch
                checked={showYoutubeTags}
                onChange={(e) => setShowYoutubeTags(e.target.checked)}
                size="small"
              />
            }
            label="顯示 YouTube 標籤"
          />
        </Box>
      </Box>

      {/* 用戶標籤區域 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LabelIcon color="primary" />
          我的標籤 ({extractAllTagsFromVideo(video).length})
        </Typography>
        
        {/* 添加新標籤 */}
        {!readOnly && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="輸入新標籤..."
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddUserTag();
                }
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddUserTag}
              disabled={!newTag.trim()}
            >
              添加
            </Button>
          </Box>
        )}

        {/* 用戶標籤列表 */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {extractAllTagsFromVideo(video).map((tag) => (
            <Chip
              key={tag}
              label={tag}
              color="primary"
              onDelete={!readOnly && userTags.includes(tag) ? () => handleDeleteUserTag(tag) : undefined}
              deleteIcon={!readOnly && userTags.includes(tag) ? <DeleteIcon /> : undefined}
              sx={{ mb: 1 }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* YouTube 標籤區域 */}
      {showYoutubeTags && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <YouTubeIcon sx={{ color: '#ff0000' }} />
            YouTube 標籤 ({youtubeHashtags.length})
          </Typography>
          
          {youtubeHashtags.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {youtubeHashtags.map((hashtag) => (
                <Chip
                  key={hashtag}
                  label={hashtag}
                  variant="outlined"
                  icon={<YouTubeIcon />}
                  onClick={!readOnly ? () => handleOpenConvertDialog(hashtag) : undefined}
                  sx={{ 
                    mb: 1,
                    cursor: !readOnly ? 'pointer' : 'default',
                    '&:hover': !readOnly ? {
                      backgroundColor: 'action.hover'
                    } : {}
                  }}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              此影片沒有 YouTube 標籤
            </Typography>
          )}
        </Box>
      )}

      {/* 轉換對話框 */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)}>
        <DialogTitle>轉換 YouTube 標籤</DialogTitle>
        <DialogContent>
          <Typography>
            是否要將 YouTube 標籤「{tagToConvert}」添加到您的標籤中？
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConvertDialogOpen(false)}>取消</Button>
          <Button onClick={handleConfirmConvert} variant="contained">
            確認轉換
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VideoTagsManager;
